import type { Severity } from "../types";

export const CHART_PALETTE = [
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#0ea5e9",
  "#a855f7",
  "#ec4899",
  "#14b8a6",
] as const;

export const SEVERITY_COLOR: Record<Severity, string> = {
  critical: "#ef4444",
  high: "#f97316",
  medium: "#f59e0b",
  low: "#10b981",
  info: "#6366f1",
};

export function resolveColor(
  source: { color?: string; severity?: Severity } | undefined,
  index: number,
): string {
  if (source?.color) return source.color;
  if (source?.severity && source.severity in SEVERITY_COLOR) {
    return SEVERITY_COLOR[source.severity];
  }
  return CHART_PALETTE[index % CHART_PALETTE.length];
}

/** Lighten (amt > 0, toward white) or darken (amt < 0, toward black) a #rrggbb
 *  hex by a fraction. Used to build the top-lit gradient sheen on pie slices. */
export function shade(hex: string, amt: number): string {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return hex;
  const num = parseInt(m[1], 16);
  const target = amt < 0 ? 0 : 255;
  const p = Math.min(1, Math.abs(amt));
  const mix = (ch: number) => Math.round((target - ch) * p + ch);
  const r = mix((num >> 16) & 255);
  const g = mix((num >> 8) & 255);
  const b = mix(num & 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

export const AXIS_TICK = { fontSize: 11, fontWeight: 500 } as const;

export const ANIM_MS = 600;

export const CHART_HEIGHT = {
  cartesian: 208,
  radial: 176,
} as const;

export const CURSOR_FILL = "rgba(148, 163, 184, 0.16)";
export const CURSOR_LINE = "rgba(148, 163, 184, 0.55)";

export const CHART_THEME_CLASS = [
  "[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground",
  "[&_.recharts-cartesian-axis-line]:stroke-border/70",
  "[&_.recharts-cartesian-grid_line]:stroke-border/60",
  "[&_.recharts-polar-grid_line]:stroke-border/60",
  "[&_.recharts-rectangle.recharts-tooltip-cursor]:fill-foreground/[0.04]",
  "[&_.recharts-curve.recharts-tooltip-cursor]:stroke-border",
  "[&_.recharts-sector[stroke='#fff']]:stroke-transparent",
  "[&_.recharts-layer]:outline-none",
  "[&_.recharts-surface]:outline-none",
].join(" ");

export function formatValue(v: number | string | null | undefined, unit = ""): string {
  const n = Number(v);
  return `${(Number.isFinite(n) ? n : 0).toLocaleString()}${unit}`;
}

export function formatCompact(v: number | string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${n}`;
}
